import React from "react";
import { Link } from "react-router-dom";
import { CreditCard, Receipt, Wallet, Users, ArrowRight, Sparkles, ShieldCheck } from "lucide-react";

const STEPS = [
  { to: "/debts?coach=1", icon: CreditCard, tone: "rose", title: "Add your first card or loan", body: "We'll walk you through it step by step — balance, APR, minimum and limit.", primary: true, cta: "Start guided setup" },
  { to: "/bills", icon: Receipt, tone: "teal", title: "Add your bills", body: "Rent, utilities, subscriptions — anything with a due date.", cta: "Add a bill" },
  { to: "/cashflow", icon: Wallet, tone: "emerald", title: "Add your income", body: "So OWEverit can spot spare cash and flag tight months early.", cta: "Add income" },
  { to: "/settings", icon: Users, tone: "indigo", title: "Invite your partner", body: "Share one plan, one calendar and one set of bills. Optional.", cta: "Open household" },
];

const TONES = {
  rose: "bg-rose-500/15 text-rose-500",
  teal: "bg-teal-500/15 text-teal-500",
  emerald: "bg-emerald-500/15 text-emerald-500",
  indigo: "bg-indigo-500/15 text-indigo-400",
};

export default function DashboardWelcome({ name }) {
  const first = (name || "").trim().split(" ")[0];
  return (
    <div className="space-y-5" data-testid="dashboard-welcome">
      <div className="relative overflow-hidden p-8 rounded-2xl border border-teal-500/30 bg-teal-500/5">
        <div className="absolute -top-16 -right-16 h-56 w-56 rounded-full bg-gradient-to-br from-teal-500/20 to-transparent blur-3xl" />
        <div className="relative">
          <div className="inline-flex items-center gap-2 text-[10px] uppercase tracking-[0.2em] text-teal-500 font-semibold"><Sparkles className="h-3.5 w-3.5" /> Welcome to OWEverit</div>
          <h2 className="font-display text-2xl sm:text-3xl font-extrabold tracking-tight mt-2">
            {first ? `Let's zero out your debt, ${first}.` : "Let's zero out your debt."}
          </h2>
          <p className="text-sm text-muted-foreground mt-2 max-w-xl">
            Your dashboard fills in the moment you add something. Start with a card or loan and OWEverit will build your fastest payoff plan, then show you exactly what to pay each month.
          </p>
          <Link to="/debts?coach=1" data-testid="welcome-primary-cta"
            className="mt-5 inline-flex items-center gap-2 px-5 py-2.5 rounded-lg bg-teal-500 text-white text-sm font-semibold hover:bg-teal-600 transition">
            Add my first debt <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {STEPS.map((s, i) => (
          <Link key={s.to} to={s.to} data-testid={`welcome-step-${i}`}
            className={`group p-5 rounded-2xl border bg-card flex items-start gap-4 hover:-translate-y-0.5 transition-all ${s.primary ? "border-teal-500/40" : "border-border hover:border-teal-500/30"}`}>
            <div className={`h-11 w-11 rounded-xl flex items-center justify-center shrink-0 ${TONES[s.tone]}`}><s.icon className="h-5 w-5" /></div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold text-muted-foreground">STEP {i + 1}</span>
              </div>
              <div className="font-display font-bold text-base mt-0.5">{s.title}</div>
              <p className="text-xs text-muted-foreground mt-1">{s.body}</p>
              <span className="mt-2 inline-flex items-center gap-1 text-xs font-semibold text-teal-500 group-hover:gap-1.5 transition-all">{s.cta} <ArrowRight className="h-3.5 w-3.5" /></span>
            </div>
          </Link>
        ))}
      </div>

      <div className="flex items-center justify-center gap-2 text-xs text-muted-foreground">
        <ShieldCheck className="h-3.5 w-3.5 text-emerald-500" /> Takes about two minutes. Nothing is shared until you choose to invite someone.
      </div>
    </div>
  );
}
